import React from 'react';
import { Box } from '@mui/material';
import PhxBadge from '_lex/_atoms/PhxBadge';
import { FieldInheritanceType } from '../../_utils/fieldComparison';
import { translate as t } from 'hooks/useTranslations';

interface FieldInheritanceBadgeProps {
  type: FieldInheritanceType;
  organizationId?: string | null;
}

/**
 * Badge component to display the inheritance status of a field in a child template
 * Shows whether a field is same as parent, modified from parent, or new in child
 * Labels differ based on template type:
 * - Global/jurisdiction templates (organizationId === null): parent/child terminology
 * - Agency templates (organizationId !== null): Lexipol/agency terminology
 */
export const FieldInheritanceBadge: React.FC<FieldInheritanceBadgeProps> = ({ type, organizationId = null }) => {
  // Use parent/child terminology for global/jurisdiction templates (organizationId === null)
  // Use Lexipol/agency terminology for agency templates (organizationId !== null)
  const translationPrefix =
    organizationId === null
      ? 'performanceReporting.templates.builder.fieldInheritance'
      : 'performanceReporting.templates.builder.fieldInheritance.agency';

  const badgeConfig = {
    'same-as-parent': {
      color: 'info' as const,
      label: t(`${translationPrefix}.sameAsParent`),
    },
    'modified-from-parent': {
      color: 'primary' as const,
      label: t(`${translationPrefix}.modifiedFromParent`),
    },
    'new-in-child': {
      color: 'success' as const,
      label: t(`${translationPrefix}.newInChild`),
    },
  };

  const config = badgeConfig[type];

  return (
    <Box sx={{ '& .lex-badge': { borderRadius: '4px' } }}>
      <PhxBadge
        color={config.color}
        size='sm'
      >
        {config.label}
      </PhxBadge>
    </Box>
  );
};

export default FieldInheritanceBadge;
